import React from 'react';
import PropTypes from 'prop-types';

/**
 * Badge3D - A modern 3D badge with depth effect
 */
const Badge3D = ({
  children,
  className = '',
  variant = 'primary',
  size = 'md',
  rounded = 'full',
  glow = false,
  depth = 2,
  ...props
}) => {
  // Base styles
  const baseStyles = 'inline-flex items-center justify-center font-medium relative';
  
  // Size styles
  const sizeStyles = {
    xs: 'px-1.5 py-0.5 text-xs',
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base'};
  
  // Rounded styles
  const roundedStyles = {
    none: 'rounded-none',
    sm: 'rounded',
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full'};
  
  // Color variants
  const variantStyles = {
    primary: 'bg-yellow-500 text-white',
    secondary: 'bg-pink-500 text-white',
    success: 'bg-emerald-500 text-white',
    warning: 'bg-amber-500 text-white',
    danger: 'bg-red-500 text-white',
    info: 'bg-cyan-500 text-white',
    dark: 'bg-gray-800 text-white',
    light: 'bg-gray-100 text-gray-800',
    purple: 'bg-purple-500 text-white',
    teal: 'bg-teal-500 text-white'};
  
  // Shadow colors for 3D effect
  const shadowColors = {
    primary: '#b45309', // yellow-700
    secondary: '#db2777', // pink-700
    success: '#047857', // emerald-700
    warning: '#b45309', // amber-700
    danger: '#b91c1c', // red-700
    info: '#0e7490', // cyan-700
    dark: '#1f2937', // gray-800
    light: '#e5e7eb', // gray-200
    purple: '#7e22ce', // purple-700
    teal: '#0f766e', // teal-700
  };
  
  // Glow effect
  const glowStyles = glow ? {
    primary: 'shadow-yellow-500/50',
    secondary: 'shadow-pink-500/50',
    success: 'shadow-emerald-500/50',
    warning: 'shadow-amber-500/50',
    danger: 'shadow-red-500/50',
    info: 'shadow-cyan-500/50',
    dark: 'shadow-gray-800/50',
    light: 'shadow-gray-300/50',
    purple: 'shadow-purple-500/50',
    teal: 'shadow-teal-500/50'}[variant] : '';

  // Create the 3D effect with multiple box shadows
  const create3DEffect = (color, depth) => {
    let shadows = [];
    for (let i = 1; i <= depth; i++) {
      shadows.push(`${i}px ${i}px 0 ${color}`);
    }
    return shadows.join(', ');
  };

  return (
    <span
      className={`${baseStyles} ${sizeStyles[size]} ${roundedStyles[rounded]} ${variantStyles[variant]} ${glowStyles} ${className}`}
      style={{ 
        boxShadow: create3DEffect(shadowColors[variant], depth),
        transform: 'translateX(-2px) translateY(-2px)'}}
      {...props}
    >
      {children}
    </span>
  );
};

Badge3D.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'danger', 'info', 'dark', 'light', 'purple', 'teal']),
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  rounded: PropTypes.oneOf(['none', 'sm', 'md', 'lg', 'full']),
  glow: PropTypes.bool,
  depth: PropTypes.number};

export default Badge3D;
